import { useMutation } from '@tanstack/react-query';
import {
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { PlantApi } from 'apis/PlantApis';
import { RewardCalculationApis } from 'apis/RewardCalculationApis';
import { Button, SelectForm } from 'components';
import { useUser } from 'contexts/UserProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { toastError, toastSuccess } from 'utils/constants/functions';

interface IProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  optionsBranch: DefaultOptionType[];
  refetchGetReward: () => void;
}

const spanCol = 12;
const ModalRewardSetup = (props: IProps) => {
  const { isOpen, setIsOpen, refetchGetReward } = props;
  const [form] = useForm();
  const { user } = useUser();
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      const plants = items.map((item: any) => ({
        label: item.id,
        value: item.id,
      }));

      setPlants(plants);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleSuccess = (res: any) => {
    if (!res?.data) {
      toastError(res?.errors[0]?.message || 'Tính thưởng thất bại');
    } else {
      toastSuccess('Tính thưởng thành công');
      setIsOpen(false);
      refetchGetReward();
    }
  };

  const handleError = (error: any) => {
    toastError(error?.message || 'Tính thưởng thất bại');
  };

  const {
    mutate: calculateRewardForPlant,
    isPending: calculateRewardForPlantLoading,
  } = useMutation({
    mutationFn: RewardCalculationApis.calculateRewardForPlant,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleOK = () => {
    const data = form.getFieldsValue();

    const plantId = [];
    if (data.plantId === 'all') {
      plantId.push('5001', '5002', '5004', '5005');
    } else {
      plantId.push(data.plantId);
    }

    console.log('OK', data);
    calculateRewardForPlant({
      plantId,
      fromMonth: Number(dayjs(data.fromDate).startOf('month').format('MM')),
      toMonth: Number(dayjs(data.toDate).endOf('month').format('MM')),
      fromYear: Number(dayjs(data.fromDate).startOf('month').format('YYYY')),
      toYear: Number(dayjs(data.toDate).endOf('month').format('YYYY')),
    });
  };

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  useEffect(() => {
    form.setFieldsValue({
      plantId: 'all',
      fromDate: dayjs().startOf('month'),
      toDate: dayjs().endOf('month'),
    });
  }, [form, isOpen]);

  return (
    <Modal
      open={isOpen}
      title={
        <Typography.Text className="text-xl font-bold">
          Điều chỉnh tính thưởng
        </Typography.Text>
      }
      footer={[
        <Flex
          justify="flex-end"
          className="bg-[#EFF3F8] !px-7 !py-3 rounded-b-xl"
          gap={10}
          key={'ModalRewardSetup'}
        >
          <Button title="Hủy" handleOnclick={handleCancel} />
          <Button
            fill
            title="Tính toán"
            handleOnclick={handleOK}
            customClass="bg-[#003056]"
            loading={calculateRewardForPlantLoading}
          />
        </Flex>,
      ]}
      onCancel={handleCancel}
      classNames={{
        content: '!p-0',
        header: '!px-7 !py-3 !m-0',
      }}
    >
      <Form form={form} layout="vertical">
        <Divider className="mt-0 mb-3" />
        <Space direction="vertical" size={0} className="px-7">
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Chi nhánh" name="plantId">
                <SelectForm
                  options={[
                    {
                      label: 'Tất cả chi nhánh',
                      value: 'all',
                    },
                    ...plants,
                  ]}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  placeholder="Chọn chi nhánh"
                  loading={getAllPlantLoading}
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol}></Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item
                label="Từ tháng"
                name="fromDate"
                rules={[{ required: true, message: '' }]}
              >
                <DatePicker
                  placeholder="Chọn tháng"
                  picker="month"
                  format={'MM/YYYY'}
                  size="large"
                  className="w-full"
                  allowClear={false}
                  onChange={(value) => setFromDate(value)}
                  maxDate={toDate || dayjs()}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item
                label="Đến tháng"
                name="toDate"
                rules={[{ required: true, message: '' }]}
              >
                <DatePicker
                  placeholder="Chọn tháng"
                  picker="month"
                  format={'MM/YYYY'}
                  size="large"
                  className="w-full"
                  allowClear={false}
                  maxDate={dayjs()}
                  onChange={(value) => setToDate(value)}
                  minDate={fromDate || undefined}
                />
              </Form.Item>
            </Col>
          </Row>
        </Space>
      </Form>
    </Modal>
  );
};

export default ModalRewardSetup;
